import React, { useState } from 'react';

import styled from 'styled-components';

import ChevronRight from 'common/Icons/ChevronRight';
import { COMMUNICATION_CHANNEL_COLORS } from 'constants/communicationTags';
import { SpeedToLeadTouchpointEvent } from 'types/recommendations';

import { SPEED_TO_LEAD_FOLLOW_UP_VIEW, SPEED_TO_LEAD_TOUCH_LABEL } from './constants';
import { getChannelLabel, getEventChannel } from './speedToLeadOverallUtils';
import SpeedToLeadTouchpointContent from './SpeedToLeadTouchpointContent';
import { TouchpointPreviewSelection } from './SpeedToLeadTouchpointPreviewModal.types';
import { formatElapsed, formatTime } from './touchpointUtils';

const TouchpointCard = styled.article<{ selected: boolean }>`
  overflow: hidden;
  border: 1px solid ${({ selected }) => (selected ? 'var(--dark-blue)' : 'var(--line-gray)')};
  border-radius: 20px;
  background: var(--neutral-colors-gray-10);
  box-shadow: 0 2px 4px 0 var(--box-shadow-gray-dark);
`;

const TouchpointHeader = styled.button`
  display: flex;
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  align-items: center;
  gap: 9px;
  padding: 0 12px;
  border: 0;
  background: var(--surface-muted-dark);
  color: var(--white);
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    outline: 2px solid var(--dark-blue);
    outline-offset: -2px;
  }
`;

const ExpandIcon = styled.span<{ expanded: boolean }>`
  display: inline-flex;
  flex: none;
  transform: rotate(${({ expanded }) => (expanded ? '90deg' : '0deg')});
  transition: transform 120ms ease;
`;

const ChannelBadge = styled.span<{ channel: string }>`
  flex: none;
  padding: 3px 8px;
  border-radius: 10px;
  background: ${({ channel }) => COMMUNICATION_CHANNEL_COLORS[channel] ?? 'var(--text-secondary)'};
  color: var(--white);
  font-family: var(--font-bold);
  font-size: 10px;
  text-transform: uppercase;
`;

const Timing = styled.span`
  overflow: hidden;
  color: var(--white);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type Props = {
  event: SpeedToLeadTouchpointEvent;
  eventKey: string | number;
  events: SpeedToLeadTouchpointEvent[];
  mode: TouchpointPreviewSelection['mode'];
  defaultExpanded: boolean;
  selected?: boolean;
  cardRef?: React.RefObject<HTMLElement | null>;
};

const SpeedToLeadTouchpoint = ({
  event,
  eventKey,
  events,
  mode,
  defaultExpanded,
  selected = false,
  cardRef,
}: Props) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const eventChannel = getEventChannel(event);
  const relevantEvents = events.filter(
    (candidate) => mode === SPEED_TO_LEAD_FOLLOW_UP_VIEW.SEQUENCE || getEventChannel(candidate) === eventChannel
  );
  const eventIndex = relevantEvents.indexOf(event);
  const previousEvent = eventIndex > 0 ? relevantEvents[eventIndex - 1] : null;
  const elapsedSeconds = previousEvent ? event.elapsed_seconds - previousEvent.elapsed_seconds : event.elapsed_seconds;
  const previousTouchLabel =
    mode === SPEED_TO_LEAD_FOLLOW_UP_VIEW.CHANNEL
      ? getChannelLabel(eventChannel).toLowerCase()
      : SPEED_TO_LEAD_TOUCH_LABEL;
  const reference = previousEvent ? `after the previous ${previousTouchLabel}` : 'after form submission';

  return (
    <TouchpointCard ref={cardRef} selected={selected} data-test-id={`speed-to-lead-touchpoint-${eventKey}`}>
      <TouchpointHeader
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        data-test-id={`speed-to-lead-touchpoint-toggle-${eventKey}`}
      >
        <ExpandIcon expanded={expanded}>
          <ChevronRight fill="var(--white)" width={20} height={20} />
        </ExpandIcon>
        <ChannelBadge channel={eventChannel}>{getChannelLabel(eventChannel)}</ChannelBadge>
        <Timing>
          {formatElapsed(elapsedSeconds)} {reference} · {formatTime(event.occurred_at)}
        </Timing>
      </TouchpointHeader>
      {expanded && <SpeedToLeadTouchpointContent event={event} eventKey={eventKey} />}
    </TouchpointCard>
  );
};

export default SpeedToLeadTouchpoint;
